import { isArray, isObject } from 'vega-util';
import { isBinned, isBinning } from '../../bin';
import { X } from '../../channel';
import { isDiscrete, isFieldDef, toFieldDefBase, valueArray } from '../../channeldef';
import { hasDiscreteDomain } from '../../scale';
import { durationExpr, normalizeTimeUnit } from '../../timeunit';
import { NOMINAL, ORDINAL } from '../../type';
import { contains, normalizeAngle } from '../../util';
import { isSignalRef } from '../../vega.schema';
import { mergeTitle, mergeTitleFieldDefs } from '../common';
import { getAxisConfig } from './config';
export var axisRules = {
  scale: function scale(_ref) {
    var model = _ref.model,
      channel = _ref.channel;
    return model.scaleName(channel);
  },
  format: function format(_ref2) {
    var _format = _ref2.format;
    return _format;
  },
  // we already calculate this in parse
  formatType: function formatType(_ref3) {
    var _formatType = _ref3.formatType;
    return _formatType;
  },
  // we already calculate this in parse
  grid: function grid(_ref4) {
    var _axis$grid;
    var fieldOrDatumDef = _ref4.fieldOrDatumDef,
      axis = _ref4.axis,
      scaleType = _ref4.scaleType;
    return (_axis$grid = axis.grid) !== null && _axis$grid !== void 0 ? _axis$grid : defaultGrid(scaleType, fieldOrDatumDef);
  },
  gridScale: function gridScale(_ref5) {
    var model = _ref5.model,
      channel = _ref5.channel;
    return _gridScale(model, channel);
  },
  labelAlign: function labelAlign(_ref6) {
    var axis = _ref6.axis,
      labelAngle = _ref6.labelAngle,
      orient = _ref6.orient,
      channel = _ref6.channel;
    return axis.labelAlign || defaultLabelAlign(labelAngle, orient, channel);
  },
  labelAngle: function labelAngle(_ref7) {
    var _labelAngle = _ref7.labelAngle;
    return _labelAngle;
  },
  // we already calculate this in parse
  labelBaseline: function labelBaseline(_ref8) {
    var axis = _ref8.axis,
      labelAngle = _ref8.labelAngle,
      orient = _ref8.orient,
      channel = _ref8.channel;
    return axis.labelBaseline || defaultLabelBaseline(labelAngle, orient, channel);
  },
  labelFlush: function labelFlush(_ref9) {
    var _axis$labelFlush;
    var axis = _ref9.axis,
      fieldOrDatumDef = _ref9.fieldOrDatumDef,
      channel = _ref9.channel;
    return (_axis$labelFlush = axis.labelFlush) !== null && _axis$labelFlush !== void 0 ? _axis$labelFlush : defaultLabelFlush(fieldOrDatumDef.type, channel);
  },
  labelOverlap: function labelOverlap(_ref10) {
    var _axis$labelOverlap;
    var axis = _ref10.axis,
      fieldOrDatumDef = _ref10.fieldOrDatumDef,
      scaleType = _ref10.scaleType;
    return (_axis$labelOverlap = axis.labelOverlap) !== null && _axis$labelOverlap !== void 0 ? _axis$labelOverlap : defaultLabelOverlap(fieldOrDatumDef.type, scaleType, isFieldDef(fieldOrDatumDef) && !!fieldOrDatumDef.timeUnit, isFieldDef(fieldOrDatumDef) ? fieldOrDatumDef.sort : undefined);
  },
  // we already calculate orient in parse
  orient: function orient(_ref11) {
    var _orient = _ref11.orient;
    return _orient;
  },
  // Need to cast until Vega supports signal
  tickCount: function tickCount(_ref12) {
    var _axis$tickCount;
    var channel = _ref12.channel,
      model = _ref12.model,
      axis = _ref12.axis,
      fieldOrDatumDef = _ref12.fieldOrDatumDef,
      scaleType = _ref12.scaleType;
    var sizeType = channel === 'x' ? 'width' : channel === 'y' ? 'height' : undefined;
    var size = sizeType ? model.getSizeSignalRef(sizeType) : undefined;
    return (_axis$tickCount = axis.tickCount) !== null && _axis$tickCount !== void 0 ? _axis$tickCount : defaultTickCount({
      fieldOrDatumDef: fieldOrDatumDef,
      scaleType: scaleType,
      size: size,
      values: axis.values
    });
  },
  tickMinStep: defaultTickMinStep,
  title: function title(_ref13) {
    var axis = _ref13.axis,
      model = _ref13.model,
      channel = _ref13.channel;
    if (axis.title !== undefined) {
      return axis.title;
    }
    var fieldDefTitle = getFieldDefTitle(model, channel);
    if (fieldDefTitle !== undefined) {
      return fieldDefTitle;
    }
    var fieldDef = model.typedFieldDef(channel);
    var channel2 = channel === 'x' ? 'x2' : 'y2';
    var fieldDef2 = model.fieldDef(channel2);
    // If title not specified, store base parts of fieldDef (and fieldDef2 if exists)
    return mergeTitleFieldDefs(fieldDef ? [toFieldDefBase(fieldDef)] : [], isFieldDef(fieldDef2) ? [toFieldDefBase(fieldDef2)] : []);
  },
  values: function values(_ref14) {
    var axis = _ref14.axis,
      fieldOrDatumDef = _ref14.fieldOrDatumDef;
    return _values(axis, fieldOrDatumDef);
  },
  zindex: function zindex(_ref15) {
    var _axis$zindex;
    var axis = _ref15.axis,
      fieldOrDatumDef = _ref15.fieldOrDatumDef,
      mark = _ref15.mark;
    return (_axis$zindex = axis.zindex) !== null && _axis$zindex !== void 0 ? _axis$zindex : defaultZindex(mark, fieldOrDatumDef);
  }
};
// TODO: we need to refactor this method after we take care of config refactoring
/**
 * Default rules for whether to show a grid should be shown for a channel.
 * If `grid` is unspecified, the default value is `true` for ordinal scales that are not binned
 */
export function defaultGrid(scaleType, fieldDef) {
  return !hasDiscreteDomain(scaleType) && isFieldDef(fieldDef) && !isBinning(fieldDef === null || fieldDef === void 0 ? void 0 : fieldDef.bin) && !isBinned(fieldDef === null || fieldDef === void 0 ? void 0 : fieldDef.bin);
}
function _gridScale(model, channel) {
  var gridChannel = channel === 'x' ? 'y' : 'x';
  if (model.getScaleComponent(gridChannel)) {
    return model.scaleName(gridChannel);
  }
  return undefined;
}
export { _gridScale as gridScale };
export function getLabelAngle(fieldOrDatumDef, axis, channel, styleConfig, axisConfigs) {
  var labelAngle = axis === null || axis === void 0 ? void 0 : axis.labelAngle;
  // try axis value
  if (labelAngle !== undefined) {
    return isSignalRef(labelAngle) ? labelAngle : normalizeAngle(labelAngle);
  } else {
    // try axis config value
    var _getAxisConfig = getAxisConfig('labelAngle', styleConfig, axis === null || axis === void 0 ? void 0 : axis.style, axisConfigs),
      angle = _getAxisConfig.configValue;
    if (angle !== undefined) {
      return normalizeAngle(angle);
    } else {
      // get default value
      if (channel === X && contains([NOMINAL, ORDINAL], fieldOrDatumDef.type) && !(isFieldDef(fieldOrDatumDef) && fieldOrDatumDef.timeUnit)) {
        return 270;
      }
      // no default
      return undefined;
    }
  }
}
export function normalizeAngleExpr(angle) {
  return "(((".concat(angle.signal, " % 360) + 360) % 360)");
}
export function defaultLabelBaseline(angle, orient, channel, alwaysIncludeMiddle) {
  if (angle !== undefined) {
    if (channel === 'x') {
      if (isSignalRef(angle)) {
        var a = normalizeAngleExpr(angle);
        var orientIsTop = isSignalRef(orient) ? "(".concat(orient.signal, " === \"top\")") : orient === 'top';
        return {
          signal: "(45 < ".concat(a, " && ").concat(a, " < 135) || (225 < ").concat(a, " && ").concat(a, " < 315) ? \"middle\" :") + "(".concat(a, " <= 45 || 315 <= ").concat(a, ") === ").concat(orientIsTop, " ? \"bottom\" : \"top\"")
        };
      }
      if (45 < angle && angle < 135 || 225 < angle && angle < 315) {
        return 'middle';
      }
      if (isSignalRef(orient)) {
        var op = angle <= 45 || 315 <= angle ? '===' : '!==';
        return {
          signal: "".concat(orient.signal, " ").concat(op, " \"top\" ? \"bottom\" : \"top\"")
        };
      }
      return (angle <= 45 || 315 <= angle) === (orient === 'top') ? 'bottom' : 'top';
    } else {
      if (isSignalRef(angle)) {
        var _a = normalizeAngleExpr(angle);
        var orientIsLeft = isSignalRef(orient) ? "(".concat(orient.signal, " === \"left\")") : orient === 'left';
        var middle = alwaysIncludeMiddle ? '"middle"' : 'null';
        return {
          signal: "".concat(_a, " <= 45 || 315 <= ").concat(_a, " || (135 <= ").concat(_a, " && ").concat(_a, " <= 225) ? ").concat(middle, " : (45 <= ").concat(_a, " && ").concat(_a, " <= 135) === ").concat(orientIsLeft, " ? \"top\" : \"bottom\"")
        };
      }
      if (angle <= 45 || 315 <= angle || 135 <= angle && angle <= 225) {
        return alwaysIncludeMiddle ? 'middle' : null;
      }
      if (isSignalRef(orient)) {
        var _op = 45 <= angle && angle <= 135 ? '===' : '!==';
        return {
          signal: "".concat(orient.signal, " ").concat(_op, " \"left\" ? \"top\" : \"bottom\"")
        };
      }
      return (45 <= angle && angle <= 135) === (orient === 'left') ? 'top' : 'bottom';
    }
  }
  return undefined;
}
export function defaultLabelAlign(angle, orient, channel) {
  if (angle === undefined) {
    return undefined;
  }
  var isX = channel === 'x';
  var startAngle = isX ? 0 : 90;
  var mainOrient = isX ? 'bottom' : 'left';
  if (isSignalRef(angle)) {
    var a = normalizeAngleExpr(angle);
    var orientIsMain = isSignalRef(orient) ? "(".concat(orient.signal, " === \"").concat(mainOrient, "\")") : orient === mainOrient;
    return {
      signal: "(".concat(startAngle ? "(".concat(a, " + 90)") : a, " % 180 === 0) ? ").concat(isX ? null : '"center"', " :") + "(".concat(startAngle, " < ").concat(a, " && ").concat(a, " < ").concat(180 + startAngle, ") === ").concat(orientIsMain, " ? \"left\" : \"right\"")
    };
  }
  if ((angle + startAngle) % 180 === 0) {
    // For bottom, use default label align so label flush still works
    return isX ? null : 'center';
  }
  if (isSignalRef(orient)) {
    var op = startAngle < angle && angle < 180 + startAngle ? '===' : '!==';
    var _orientIsMain = "".concat(orient.signal, " ").concat(op, " \"").concat(mainOrient, "\"");
    return {
      signal: "".concat(_orientIsMain, " ? \"left\" : \"right\"")
    };
  }
  if ((startAngle < angle && angle < 180 + startAngle) === (orient === mainOrient)) {
    return 'left';
  }
  return 'right';
}
export function defaultLabelFlush(type, channel) {
  if (channel === 'x' && contains(['quantitative', 'temporal'], type)) {
    return true;
  }
  return undefined;
}
export function defaultLabelOverlap(type, scaleType, hasTimeUnit, sort) {
  // do not prevent overlap for nominal data because there is no way to infer what the missing labels are
  if (hasTimeUnit && !isObject(sort) || type !== 'nominal' && type !== 'ordinal') {
    if (scaleType === 'log' || scaleType === 'symlog') {
      return 'greedy';
    }
    return true;
  }
  return undefined;
}
export function defaultOrient(channel) {
  return channel === 'x' ? 'bottom' : 'left';
}
export function defaultTickCount(_ref16) {
  var fieldOrDatumDef = _ref16.fieldOrDatumDef,
    scaleType = _ref16.scaleType,
    size = _ref16.size,
    vals = _ref16.values;
  if (!vals && !hasDiscreteDomain(scaleType) && scaleType !== 'log') {
    if (isFieldDef(fieldOrDatumDef)) {
      var _normalizeTimeUnit;
      if (isBinning(fieldOrDatumDef.bin)) {
        // for binned data, we don't want more ticks than maxbins
        return {
          signal: "ceil(".concat(size.signal, "/10)")
        };
      }
      if (fieldOrDatumDef.timeUnit && contains(['month', 'hours', 'day', 'quarter'], (_normalizeTimeUnit = normalizeTimeUnit(fieldOrDatumDef.timeUnit)) === null || _normalizeTimeUnit === void 0 ? void 0 : _normalizeTimeUnit.unit)) {
        return undefined;
      }
    }
    return {
      signal: "ceil(".concat(size.signal, "/40)")
    };
  }
  return undefined;
}
export function defaultTickMinStep(_ref17) {
  var format = _ref17.format,
    fieldOrDatumDef = _ref17.fieldOrDatumDef;
  if (format === 'd') {
    return 1;
  }
  if (isFieldDef(fieldOrDatumDef)) {
    var timeUnit = fieldOrDatumDef.timeUnit;
    if (timeUnit) {
      var signal = durationExpr(timeUnit);
      if (signal) {
        return {
          signal: signal
        };
      }
    }
  }
  return undefined;
}
export function getFieldDefTitle(model, channel) {
  var channel2 = channel === 'x' ? 'x2' : 'y2';
  var fieldDef = model.fieldDef(channel);
  var fieldDef2 = model.fieldDef(channel2);
  var title1 = fieldDef ? fieldDef.title : undefined;
  var title2 = fieldDef2 ? fieldDef2.title : undefined;
  if (title1 && title2) {
    return mergeTitle(title1, title2);
  } else if (title1) {
    return title1;
  } else if (title2) {
    return title2;
  } else if (title1 !== undefined) {
    // falsy value to disable config
    return title1;
  } else if (title2 !== undefined) {
    // falsy value to disable config
    return title2;
  }
  return undefined;
}
function _values(axis, fieldOrDatumDef) {
  var vals = axis.values;
  if (isArray(vals)) {
    return valueArray(fieldOrDatumDef, vals);
  } else if (isSignalRef(vals)) {
    return vals;
  }
  return undefined;
}
export { _values as values };
export function defaultZindex(mark, fieldDef) {
  if (mark === 'rect' && isDiscrete(fieldDef)) {
    return 1;
  }
  return 0;
}